import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  ErrorMessagePosition,
  formInputFieldName,
  InputType
} from './form-input-constants';
import { getTitle } from './FakeInputHelper';
import { TypeAttributes } from 'rsuite/cjs/internals/types';

export interface IDefaultInputProps {
  formik: any;
  fieldName: formInputFieldName;
  inputType: InputType | string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  min?: number;
  max?: number;
  shouldFormat?: boolean;
  suffix?: string;
  prefix?: string;
  label?: string;
  additionalClasses?: string;
  isLabelIcon?: boolean;
  tooltipText?: string;
  tooltipPlacement?: TypeAttributes.Placement;
  errorMessagePosition?: ErrorMessagePosition;
  fieldLabel?: React.ReactNode;
}

export const DefaultInput: React.FC<IDefaultInputProps> = (props) => {
  const {
    formik,
    fieldName,
    inputType,
    placeholder,
    disabled,
    readOnly,
    autoComplete,
    min,
    max,
    shouldFormat,
    suffix,
    prefix,
    label,
    fieldLabel,
    additionalClasses,
    isLabelIcon = false,
    tooltipText,
    tooltipPlacement,
    errorMessagePosition = {
      top: '4.9rem',
      left: '0.7rem',
      right: 'unset',
      bottom: 'unset'
    }
  } = props;

  console.log('DefaultInput rendered', suffix);

  const { t } = useTranslation();
  const { top = '4.9rem', left = '0.6rem', right = 'unset', bottom = 'unset' } = errorMessagePosition;

  // Helper function to format number with commas
  const formatNumberWithCommas = (value: string | number): string => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(numValue)) return String(value);
    return new Intl.NumberFormat('en-US').format(numValue);
  };

  // Helper function to remove commas from formatted number
  const removeCommas = (value: string): string => {
    return value.replace(/,/g, '');
  };

  // Get display value for formatted inputs
  const getDisplayValue = (value: any): string => {
    if (shouldFormat && inputType === InputType.Number) {
      return formatNumberWithCommas(value);
    }
    return value || '';
  };

  const handleChange = (e): void => {
    const inputValue = e.target.value;

    // For formatted number inputs
    if (shouldFormat && inputType === InputType.Number) {
      // Remove commas and validate that it's a valid number
      const rawValue = removeCommas(inputValue);

      // Only allow numbers, empty string, or single decimal point
      if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
        if (min !== undefined && max !== undefined) {
          const numValue = rawValue === '' ? '' : Number(rawValue);
          if (rawValue === '' || (numValue >= min && numValue <= max)) {
            formik.setFieldValue(fieldName, rawValue);
          }
        } else {
          formik.setFieldValue(fieldName, rawValue);
        }
      }

      return;
    }

    if (min !== undefined && max !== undefined && inputType === InputType.Number) {
      const numValue = inputValue === '' ? '' : Number(inputValue);
      if (inputValue === '' || (numValue >= min && numValue <= max)) {
        formik.handleChange(e);
      }

      return;
    }

    if (min !== undefined && inputType === InputType.Number) {
        const numValue = inputValue === '' ? '' : Number(inputValue);
        if (inputValue === '' || numValue >= min) {
            formik.handleChange(e);
        }

        return;
    }

    if (max !== undefined && inputType === InputType.Number) {
      const numValue = inputValue === '' ? '' : Number(inputValue);
      if (inputValue === '' || numValue <= max) {
        formik.handleChange(e);
      }

      return;
    }

    // TODO check can we remove
    if (inputType === InputType.Number && fieldName === 'annualSalesVolume' as formInputFieldName) {
      // For annualSalesVolume, store as string to handle large numbers
      formik.setFieldValue(fieldName, inputValue);
    } else {
      formik.handleChange(e);
    }
  };

  const handleKeyDown = (e): void => {
    // For formatted number inputs, only allow numeric keys, backspace, delete, arrow keys, etc.
    if (shouldFormat && inputType === InputType.Number) {
      const allowedKeys = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'Tab', 'Enter', 'Escape', '.'
      ];

      const isNumber = /^[0-9]$/.test(e.key);
      const isAllowedKey = allowedKeys.includes(e.key);
      const isCtrlCmd = e.ctrlKey || e.metaKey;

      if (!isNumber && !isAllowedKey && !isCtrlCmd) {
        e.preventDefault();
      }
    }
  };

  return (
    <div className={clsx('col-6 col-sm-4 col-md-3 fv-row input-container mb-5', additionalClasses)}>
      {getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t })}
      <div className="position-relative">
        <input
          {...formik.getFieldProps(fieldName)}
          type={shouldFormat && inputType === InputType.Number ? 'text' : inputType}
          className={clsx(
            'form-control form-control-sm form-control-solid mb-3 border-0',
            inputType === InputType.Number && 'number-input-zip'
          )}
          placeholder={placeholder ? placeholder : ''}
          disabled={Boolean(disabled)}
          readOnly={readOnly}
          id={fieldName}
          autoComplete={autoComplete ?? 'Off'}
          min={min}
          max={max}
          value={getDisplayValue(formik.getFieldProps(fieldName).value)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {suffix && <span
          className="position-absolute fs-8 end-0 top-50 translate-middle-y me-3">{suffix}</span>}
      </div>
      {formik.touched[fieldName] && formik.errors[fieldName] && (
        <div className="error-container" style={{ top, left, right, bottom }}>
          <div className="text-danger fs-8">{formik.errors[fieldName]}</div>
        </div>
      )}
    </div>
  );
};
